import axios, {
  AxiosError,
  AxiosHeaders,
  AxiosRequestConfig,
  InternalAxiosRequestConfig,
} from 'axios';
import queryString from 'query-string';
import { AuthProps } from '../../types/auth/auth';
import { authStorage } from './authStorage';
import { parseApiJson } from './apiDataUtils';
import {
  AUTH_ACCEPT_INVITE,
  AUTH_LOGIN,
  AUTH_REFRESH,
  AUTH_REGISTER,
} from './endpointsConstants';
import { dispatchRateLimitAlert, getRateLimitMessage, isRateLimitError } from './rateLimit';

type ApiRequestConfig = AxiosRequestConfig & {
  _retry?: boolean;
  skipAuthRefresh?: boolean;
};

type RetryQueueItem = {
  resolve: (token: string) => void;
  reject: (error: unknown) => void;
};

const publicEndpoints = [
  `/${AUTH_LOGIN}`,
  `/${AUTH_REGISTER}`,
  `/${AUTH_ACCEPT_INVITE}`,
  `/${AUTH_REFRESH}`,
];

const apiClient = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  headers: {
    'Content-type': 'application/json',
  },
});

const refreshClient = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  headers: {
    'Content-type': 'application/json',
  },
});

let isRefreshing = false;
let failedQueue: RetryQueueItem[] = [];

const isPublicEndpoint = (url?: string) => {
  if (!url) {
    return false;
  }

  return publicEndpoints.some((endpoint) => url.endsWith(endpoint) || url.includes(`${endpoint}?`));
};

const setAuthorizationHeader = (
  config: InternalAxiosRequestConfig | ApiRequestConfig,
  token: string,
) => {
  if (config.headers instanceof AxiosHeaders) {
    config.headers.set('Authorization', `Bearer ${token}`);
    return;
  }

  const headers = AxiosHeaders.from(config.headers as any);
  headers.set('Authorization', `Bearer ${token}`);
  config.headers = headers as any;
};

const processQueue = (error: unknown, token?: string) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error || !token) {
      reject(error);
      return;
    }

    resolve(token);
  });

  failedQueue = [];
};

const redirectToLogin = () => {
  if (typeof window === 'undefined') {
    return;
  }

  if (window.location.pathname !== '/login') {
    window.location.replace('/login');
  }
};

apiClient.interceptors.request.use((config) => {
  if (isPublicEndpoint(config.url)) {
    return config;
  }

  const token = authStorage.getToken();
  if (token) {
    setAuthorizationHeader(config, token);
  }

  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as ApiRequestConfig | undefined;

    if (isRateLimitError(error)) {
      if (!isPublicEndpoint(originalRequest?.url)) {
        dispatchRateLimitAlert(
          getRateLimitMessage(error, 'Too many requests. Please try again later.'),
        );
      }

      return Promise.reject(error);
    }

    if (
      !originalRequest ||
      error.response?.status !== 401 ||
      originalRequest._retry ||
      originalRequest.skipAuthRefresh ||
      isPublicEndpoint(originalRequest.url)
    ) {
      return Promise.reject(error);
    }

    const refreshToken = authStorage.getRefreshToken();
    if (!refreshToken) {
      authStorage.clearAuth();
      redirectToLogin();
      return Promise.reject(error);
    }

    if (isRefreshing) {
      return new Promise<string>((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      }).then((token) => {
        setAuthorizationHeader(originalRequest, token);
        return apiClient(originalRequest);
      });
    }

    originalRequest._retry = true;
    isRefreshing = true;

    try {
      const refreshResponse = await refreshClient.post<unknown>(
        `/${AUTH_REFRESH}?token=${encodeURIComponent(refreshToken)}`,
      );
      const authData = parseApiJson<AuthProps>(refreshResponse.data);

      if (!authData?.token || !authData.refreshToken) {
        throw new Error('Invalid refresh response');
      }

      authStorage.setAuth(authData);
      processQueue(null, authData.token);
      setAuthorizationHeader(originalRequest, authData.token);

      return apiClient(originalRequest);
    } catch (refreshError) {
      processQueue(refreshError);
      authStorage.clearAuth();
      redirectToLogin();
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  },
);

const Get = async <T>(url: string, axiosConfig?: AxiosRequestConfig) => {
  const response = await apiClient.get<T>(url, axiosConfig);
  return response.data;
};

const List = async <T>(url: string, parameters?: any) => {
  const bakedUrl = parameters ? url + '?' + queryString.stringify(parameters) : url;
  const response = await apiClient.get<T>(bakedUrl);
  return response.data;
};

const Post = async <T>(url: string, item: any, config?: any) => {
  const response = await apiClient.post<T>(url, item, config);
  return response.data;
};

const Put = async <T>(url: string, item: any) => {
  const response = await apiClient.put<T>(url, item);
  return response.data;
};

const Patch = async <T>(url: string, item: any) => {
  const response = await apiClient.patch<T>(url, item);
  return response.data;
};

const Delete = async <T>(url: string, item: any) => {
  const response = await apiClient.delete<T>(url, { data: item });
  // return response.data;
  return {
    status: response.status,
    data: response.data,
  };
};

const BaseServerInteractionService = {
  Get,
  List,
  Post,
  Put,
  Patch,
  Delete,
};

export { apiClient };
export default BaseServerInteractionService;
